<template>
    <DialogShell :model-value="modelValue" :disabled="isWorking" class="sm:max-w-md" @update:model-value="$emit('update:modelValue', $event)">
        <DialogHeader>
            <DialogTitle>{{ $t('views.farmToFarm.export.title') }}</DialogTitle>
            <DialogDescription>{{ $t('views.farmToFarm.export.description') }}</DialogDescription>
        </DialogHeader>

        <!-- RadioGroup lays out horizontally by default; these two labels are long enough to stack. -->
        <RadioGroup v-model="scope" :disabled="isWorking" orientation="vertical" class="flex-col gap-y-2">
            <div class="flex items-center" :class="cursorClass">
                <RadioGroupItem id="farm-to-farm-export-all" value="all" />
                <FieldLabel for="farm-to-farm-export-all" class="font-normal pl-2">
                    {{ $t('views.farmToFarm.export.allPeriods', { count: periods.length }) }}
                </FieldLabel>
            </div>
            <div class="flex items-center" :class="cursorClass">
                <RadioGroupItem id="farm-to-farm-export-current" value="current" />
                <FieldLabel for="farm-to-farm-export-current" class="font-normal pl-2">
                    {{ $t('views.farmToFarm.export.currentPeriod', { period: currentPeriodLabel }) }}
                </FieldLabel>
            </div>
        </RadioGroup>

        <DialogFooter>
            <DialogClose as-child>
                <CustomButton color-preset="white" :disabled="isWorking">
                    {{ $t('views.farmToFarm.export.cancel') }}
                </CustomButton>
            </DialogClose>
            <CustomButton :loading="isWorking" @click="$emit('download', scope)">
                {{ $t('views.farmToFarm.export.download') }}
            </CustomButton>
        </DialogFooter>
    </DialogShell>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
// `Dialog` and `Label` are aliased: both shadow reserved HTML element names, and
// vue/no-reserved-component-names only inspects locally registered names - so the sibling modals
// get away with the plain names purely because they use <script setup>, where the rule cannot see
// the registration. RadioGroup and RadioGroupItem are not element names and need no alias.
import { Dialog as DialogShell, DialogClose, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label as FieldLabel } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import CustomButton from '@/components/ui/CustomButton.vue';

/**
 * Chooses what to export. The workbook itself is built by the page, because it owns the report.
 *
 * No request is made: every selected period is already in the fetched report, so both options
 * are pure client-side work.
 *
 * Note the local Dialog wrapper is not the stock shadcn one - it takes `modelValue`, not `open`,
 * and renders its own DialogContent around the default slot. Passing `:open` leaves it closed
 * for good, and nesting a DialogContent inside it portals a second overlay over the first.
 */
export default defineComponent({
    components: { CustomButton, DialogShell, DialogClose, DialogDescription, DialogFooter, DialogHeader, DialogTitle, FieldLabel, RadioGroup, RadioGroupItem },
    props: {
        modelValue: { type: Boolean, required: true },
        periods: { type: Array as PropType<string[]>, required: true },
        /** Already localised by the parent; shown, never used as a key. */
        currentPeriodLabel: { type: String, required: true },
        isWorking: { type: Boolean, default: false },
    },
    emits: ['update:modelValue', 'download'],
    data() {
        return {
            scope: 'all' as 'all' | 'current',
        };
    },
    computed: {
        // RadioGroupItem carries its own disabled styling; this is for the label and the row around it.
        cursorClass(): string {
            return this.isWorking ? 'cursor-not-allowed' : 'cursor-pointer';
        },
    },
});
</script>
